import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAuthStore } from '@/features/auth/stores/authStore';
import type { User } from '@/features/auth/types/auth.types';
import { isAdminOnlySession } from '@/shared/utils/authSessionUtils';

/**
 * Permissions 타입 정의
 */
export interface Permissions {
    isAuthEnabled: boolean;
    menus: Record<string, MenuPermission>;
    SpecialFeature: Record<string, SpecialFeaturePermission>;
    /**
     * 부서 L_DEPARTMENT_PRIV에 My Lettaga(PRV025~028) AUTH_CD 행이 하나라도 있으면 true.
     * 없거나 구세션(undefined)이면 사이드바/버튼은 ML 미저장으로 보고 통과.
     */
    mylSaved?: boolean;
}

export interface MenuPermission {
    view: boolean;
    menuName?: string | null;
    [buttonName: string]: ButtonPermission | boolean | string | null | undefined;
}

export interface ButtonPermission {
    buttonCd: string;
    privCd: string;
    allowed: boolean;
}

export interface SpecialFeaturePermission {
    use: boolean;
}

/**
 * Session Store
 * 세션 정보를 관리하는 스토어
 */
export const useSessionStore = defineStore('session', () => {
    // State
    const sessionId = ref<string | null>(null);
    const isAuthenticated = ref(false);
    const user = ref<(User & { permissions?: Permissions }) | null>(null);
    const sessionData = ref<any>(null);
    const permissions = ref<Permissions | null>(null);
    const isLoading = ref(false);
    const error = ref<string | null>(null);
    const isInitialized = ref(false);
    const initializationPromise = ref<Promise<void> | null>(null); // 동시 호출 방지용
    /** clearSession 시 증가 — 진행 중인 initializeSession 완료 시 isInitialized 덮어쓰기 방지 */
    const sessionInitEpoch = ref(0);
    const lastAuthCheckTime = ref<number>(0); // 마지막 인증 체크 시간 (중복 호출 방지)
    const AUTH_CHECK_DEBOUNCE_MS = 2000; // 2초 이내 중복 체크 방지
    
    // Singpass 세션 인증 상태 (세션별 3단계 완료 여부)
    // sessionId를 키로 사용하여 동일 세션에서의 성공 여부만 판단
    interface SingpassEntityAuth {
        entityUEN: string;
        entityName: string;
        filingAgentUEN: string;
        sessionId: string;
        completedAt: string; // ISO timestamp
        isCompleted: boolean;
    }
    const singpassEntityAuth = ref<Map<string, SingpassEntityAuth>>(new Map()); // 키: sessionId

    // Getters
    const getSessionId = computed(() => sessionId.value);
    const getIsAuthenticated = computed(() => isAuthenticated.value);
    const getUser = computed(() => user.value);
    const getSessionData = computed(() => sessionData.value);
    const getPermissions = computed(() => permissions.value);
    const getIsLoading = computed(() => isLoading.value);
    const getError = computed(() => error.value);

    // Actions
    /**
     * 세션 정보 초기화
     * @param currentPath - 현재 경로 (선택, 전달되지 않으면 window.location.pathname 사용)
     */
    const initializeSession = async (currentPath?: string) => {
        // 이미 초기화된 경우 스킵
        if (isInitialized.value) {
            console.log('⏭️ Session already initialized, skipping');
            return;
        }

        // 이미 초기화 중인 경우, 진행 중인 Promise 반환
        if (initializationPromise.value) {
            console.log('⏳ Session initialization already in progress, waiting...');
            await initializationPromise.value;
            return;
        }

        // public 페이지 체크 (세션 정보 불필요한 페이지)
        try {
            let path = currentPath;
            if (!path) {
                // 브라우저 환경에서만 window.location 사용
                if (typeof window !== 'undefined' && window.location) {
                    path = window.location.pathname;
                } else {
                    path = '';
                }
            }
            
            const publicPages = ['/auth/login', '/auth/register', '/auth/signup', '/signup'];
            const isPublicPage = publicPages.some(pagePath => {
                if (!path) return false;
                if (path === pagePath) return true;
                if (path.startsWith(pagePath + '/')) return true;
                return false;
            });

            if (isPublicPage) {
                console.log('🚫 Public page detected in initializeSession, skipping:', path);
                isInitialized.value = true; // 초기화 플래그는 설정하되 API는 호출하지 않음
                return;
            }
        } catch (error) {
            // 체크 실패 시 계속 진행
            console.warn('Failed to check route for public page:', error);
        }

        // 여기까지 도달했다는 것은 public 페이지가 아니라는 의미
        console.log('⚠️ initializeSession: public 페이지가 아님, API 호출 진행');
        console.trace('initializeSession 호출 스택:');
        
        // 초기화 Promise 생성 (동시 호출 방지)
        initializationPromise.value = (async () => {
            const epochAtStart = sessionInitEpoch.value;
            isLoading.value = true;
            error.value = null;
            
            // 초기화 시작 시 이전 세션 정보 초기화 (백엔드 응답 전에 깨끗한 상태로 시작)
            // 단, 이미 초기화된 경우는 유지 (중복 초기화 방지)
            if (!isInitialized.value) {
                isAuthenticated.value = false;
                user.value = null;
                sessionData.value = null;
                sessionId.value = null;
            }

            try {
                // authStore를 통해 세션 정보 가져오기
                const authStore = useAuthStore();
                console.log('📞 Calling getSessionInfo API from initializeSession');
                const response = await authStore.getSessionInfo();

                if (response.success && response.sessionData) {
                    const responseSessionData = response.sessionData;
                    
                    // 명시적으로 hasUser가 true이고 user가 존재하는 경우만 인증됨으로 처리
                    // 어드민 세션은 사용자 앱에서 미인증 처리
                    if (responseSessionData.hasUser === true && responseSessionData.user
                        && !isAdminOnlySession(responseSessionData.user)) {
                        isAuthenticated.value = true;
                        user.value = responseSessionData.user as (User & { permissions?: Permissions });
                        sessionId.value = responseSessionData.sessionId;
                        sessionData.value = responseSessionData;
                        // permissions 저장 (user 객체에 포함된 경우)
                        const u = responseSessionData.user as (User & { permissions?: Permissions });
                        if (u?.permissions) {
                            permissions.value = u.permissions;
                        }
                        console.log('✅ Session initialized with user:', {
                            hasUser: responseSessionData.hasUser,
                            userId: responseSessionData.user?.user?.code || responseSessionData.user?.user?.userId,
                            customerName: responseSessionData.user?.customer?.name,
                            hasPermissions: !!u?.permissions
                        });
                    } else if (responseSessionData.hasTempUser === true && responseSessionData.tempUser) {
                        isAuthenticated.value = true;
                        user.value = responseSessionData.tempUser;
                        sessionId.value = responseSessionData.sessionId;
                        sessionData.value = responseSessionData;
                        // tempUser에는 permissions 없음
                        permissions.value = null;
                        console.log('✅ Session initialized with temp user');
                    } else {
                        // hasUser가 false이거나 user가 없는 경우 명확하게 인증되지 않은 상태로 설정
                        isAuthenticated.value = false;
                        user.value = null;
                        sessionId.value = responseSessionData.sessionId || null;
                        sessionData.value = null;
                        permissions.value = null;
                        console.log('❌ Session initialized without user:', {
                            hasUser: responseSessionData.hasUser,
                            hasTempUser: responseSessionData.hasTempUser,
                            sessionId: responseSessionData.sessionId
                        });
                    }
                } else {
                    // 응답이 실패하거나 sessionData가 없는 경우
                    isAuthenticated.value = false;
                    user.value = null;
                    sessionData.value = null;
                    sessionId.value = null;
                    permissions.value = null;
                    console.log('❌ Session initialization failed - invalid response');
                }
            } catch (err: any) {
                // 네트워크 에러 또는 백엔드 연결 실패 감지
                const isNetworkError = err.name === 'AbortError' || 
                                      err.name === 'TypeError' || 
                                      err.message?.includes('Failed to fetch') ||
                                      err.message?.includes('NetworkError') ||
                                      err.code === 'ECONNREFUSED' ||
                                      err.code === 'ETIMEDOUT';
                
                if (isNetworkError) {
                    console.error('❌ Backend connection failed during session initialization:', err.message);
                    error.value = '백엔드 서버에 연결할 수 없습니다. 서버 상태를 확인해주세요.';
                } else {
                    error.value = err.message || '세션 확인에 실패했습니다.';
                }
                
                // 백엔드 연결 실패 시 명확하게 인증되지 않은 상태로 설정
                isAuthenticated.value = false;
                user.value = null;
                sessionData.value = null;
                sessionId.value = null;
                permissions.value = null;
            } finally {
                isLoading.value = false;
                // 로그아웃 등으로 clearSession이 먼저 실행되면 epoch가 바뀌므로 여기서 초기화 완료로 덮어쓰지 않음
                if (epochAtStart === sessionInitEpoch.value) {
                    isInitialized.value = true;
                }
                initializationPromise.value = null; // Promise 초기화
            }
        })();

        // Promise 완료 대기
        await initializationPromise.value;
    };

    /**
     * 쿠키에서 세션 ID 추출
     */
    const extractSessionIdFromCookie = (): string | null => {
        const cookies = document.cookie.split(';');
        const sessionCookie = cookies.find(cookie => 
            cookie.trim().startsWith('lettaga.sid=')
        );
        return sessionCookie ? sessionCookie.split('=')[1] : null;
    };

    /**
     * 세션 정보 업데이트
     */
    const updateSession = (sessionInfo: any) => {
        sessionId.value = sessionInfo.sessionId || extractSessionIdFromCookie();
        isAuthenticated.value = sessionInfo.authenticated || false;
        user.value = sessionInfo.user || null;
        sessionData.value = sessionInfo;
    };

    /**
     * 세션 정보 클리어
     */
    const clearSession = () => {
        sessionInitEpoch.value++;
        sessionId.value = null;
        isAuthenticated.value = false;
        user.value = null;
        sessionData.value = null;
        permissions.value = null;
        error.value = null;
        isInitialized.value = false;
        initializationPromise.value = null;
        singpassEntityAuth.value.clear();
    };

    /**
     * 세션 ID 수동 설정
     */
    const setSessionId = (id: string) => {
        sessionId.value = id;
    };

    /**
     * 사용자 정보 설정
     */
    const setUser = (userData: User) => {
        user.value = userData;
        isAuthenticated.value = true;
        // user 객체에 permissions가 포함된 경우 함께 저장
        if ((userData as any).permissions) {
            permissions.value = (userData as any).permissions;
        }
    };

    /**
     * 권한 정보 설정
     */
    const setPermissions = (permissionsData: Permissions) => {
        permissions.value = permissionsData;
    };

    /**
     * 에러 클리어
     */
    const clearError = () => {
        error.value = null;
    };

    /**
     * 세션 정보 새로고침
     * @param currentPath - 현재 경로 (선택)
     */
    const refreshSession = async (currentPath?: string) => {
        isInitialized.value = false; // 초기화 플래그 리셋
        await initializeSession(currentPath);
    };

    /**
     * 세션 초기화 완료 대기
     * 이미 초기화된 경우 즉시 반환, 초기화 중인 경우 완료까지 대기
     */
    const waitForSession = async () => {
        // 이미 초기화된 경우 즉시 반환
        if (isInitialized.value) {
            return;
        }

        // 초기화 중인 경우 완료 대기
        if (initializationPromise.value) {
            await initializationPromise.value;
            return;
        }

        // 아직 초기화가 시작되지 않은 경우, 초기화 시작 대기 (최대 5초)
        const maxWait = 5000;
        const checkInterval = 100;
        let waited = 0;

        while (!isInitialized.value && waited < maxWait) {
            if (initializationPromise.value) {
                await initializationPromise.value;
                return;
            }
            await new Promise(resolve => setTimeout(resolve, checkInterval));
            waited += checkInterval;
        }
    };

    /**
     * 세션 정보 가져오기 (전체)
     */
    const getSessionInfo = () => ({
        sessionId: sessionId.value,
        isAuthenticated: isAuthenticated.value,
        user: user.value,
        sessionData: sessionData.value,
        timestamp: new Date().toISOString()
    });

    /**
     * 세션 ID만 가져오기
     */
    const getSessionIdOnly = () => sessionId.value;

    /**
     * 사용자 ID 가져오기
     */
    const getUserId = () => {
        return user.value?.user?.code || null;
    };

    /**
     * 사용자 역할 가져오기
     */
    const getUserRole = () => {
        return user.value?.user?.grade || null;
    };

    /**
     * 고객 정보 가져오기
     */
    const getCustomerInfo = () => {
        return user.value?.customer || null;
    };

    /**
     * 사용자 정보 가져오기
     */
    const getUserInfo = () => {
        return user.value?.user || null;
    };

    /**
     * 세션 타입 가져오기
     */
    const getSessionType = () => {
        return user.value?.sessionType || null;
    };

    /**
     * 사용자 프로필 이미지 업데이트
     * @param imageUrl - 새로운 이미지 URL (Data URI 또는 URL)
     */
    const updateUserImage = (imageUrl: string | null) => {
        if (user.value?.user) {
            // null을 undefined로 변환 (타입 호환성)
            user.value.user.userImage = imageUrl ?? undefined;
            console.log('[SessionStore] userImage 업데이트 완료:', {
                imageUrl: imageUrl ? imageUrl.substring(0, 50) + '...' : null,
                imageLength: imageUrl?.length || 0
            });
        }
    };

    /**
     * 최초 방문 Welcome 모달을 본 것으로 표시 (메모리 세션 반영)
     * 서버 플래그(WELCOME_SEEN_YN) 갱신은 API 에서 처리하고, 여기선 현재 세션만 갱신.
     */
    const markWelcomeSeen = () => {
        if (user.value?.user) {
            user.value.user.welcomeSeen = true;
        }
    };

    /**
     * Singpass 세션 인증 상태 저장
     * @param authData - 인증 완료 데이터
     * sessionId를 키로 사용하여 동일 세션에서의 성공 여부만 판단
     */
    const saveSingpassEntityAuth = (authData: SingpassEntityAuth) => {
        // sessionId를 키로 사용
        if (!authData.sessionId) {
            console.error('[SessionStore] sessionId가 없어 인증 상태를 저장할 수 없습니다.');
            return;
        }
        singpassEntityAuth.value.set(authData.sessionId, authData);
        console.log('[SessionStore] Singpass 세션 인증 상태 저장:', {
            sessionId: authData.sessionId.substring(0, 10) + '...',
            entityUEN: authData.entityUEN,
            entityName: authData.entityName,
            completedAt: authData.completedAt
        });
    };

    /**
     * Singpass 세션 인증 상태 조회
     * @param sessionId - Session ID
     * @returns 인증 상태 또는 null
     * sessionId를 키로 사용하여 동일 세션에서의 성공 여부만 판단
     */
    const getSingpassEntityAuth = (sessionId: string | null): SingpassEntityAuth | null => {
        if (!sessionId) return null;
        return singpassEntityAuth.value.get(sessionId) || null;
    };

    /**
     * Singpass 세션 인증 상태 삭제
     * @param sessionId - Session ID
     */
    const removeSingpassEntityAuth = (sessionId: string | null) => {
        if (!sessionId) return;
        singpassEntityAuth.value.delete(sessionId);
        console.log('[SessionStore] Singpass 세션 인증 상태 삭제:', sessionId.substring(0, 10) + '...');
    };

    /**
     * 모든 Singpass Entity 인증 상태 초기화
     */
    const clearSingpassEntityAuth = () => {
        singpassEntityAuth.value.clear();
        console.log('[SessionStore] 모든 Singpass Entity 인증 상태 초기화');
    };

    /**
     * 인증 체크 시간 마킹 (라우터 가드에서 호출하여 중복 API 호출 방지)
     */
    const markAuthCheckTime = () => {
        lastAuthCheckTime.value = Date.now();
        console.log('🕐 Auth check time marked');
    };

    return {
        // State
        sessionId,
        isAuthenticated,
        user,
        sessionData,
        permissions,
        isLoading,
        error,
        isInitialized, // 초기화 상태 노출
        singpassEntityAuth, // Singpass Entity 인증 상태

        // Getters
        getSessionId,
        getIsAuthenticated,
        getUser,
        getSessionData,
        getPermissions,
        getIsLoading,
        getError,

        // Actions
        initializeSession,
        updateSession,
        clearSession,
        setSessionId,
        setUser,
        setPermissions,
        clearError,
        refreshSession,
        waitForSession,
        getSessionInfo,
        getSessionIdOnly,
        getUserId,
        getUserRole,
        getCustomerInfo,
        getUserInfo,
        getSessionType,
        updateUserImage,
        markWelcomeSeen,
        // Singpass Entity 인증 관련
        saveSingpassEntityAuth,
        getSingpassEntityAuth,
        removeSingpassEntityAuth,
        clearSingpassEntityAuth,
        // 중복 호출 방지
        markAuthCheckTime
    };
});
